import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock } from 'lucide-react';
import { useI18n } from '../../context/I18nContext';
import { formatDate, type BlogPost } from '../../lib/blog';
import BlogThumb from './BlogThumb';

interface BlogGuideCardProps {
  post: BlogPost;
  lang: 'fr' | 'en';
}

function formatTagLabel(tag: string): string {
  if (!tag) return tag;
  return tag.charAt(0).toUpperCase() + tag.slice(1).toLowerCase();
}

const BlogGuideCard: React.FC<BlogGuideCardProps> = ({ post, lang }) => {
  const { t } = useI18n();

  return (
    <Link
      to={`/blog/${post.slug}`}
      className="group flex overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-all duration-300 hover:-translate-y-0.5 hover:border-blue-200 hover:shadow-[0_12px_28px_-14px_rgba(37,99,235,0.28)]"
    >
      <BlogThumb tags={post.tags} size="side" className="w-14 sm:w-[4.75rem] md:w-[5.25rem]" />

      <div className="flex min-w-0 flex-1 flex-col p-3.5 sm:p-5">
        <div className="mb-1.5 hidden flex-wrap gap-1 sm:mb-2 sm:flex sm:gap-1.5">
          {post.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-700 sm:text-[11px]"
            >
              {formatTagLabel(tag)}
            </span>
          ))}
        </div>

        <h3 className="mb-1 text-base font-bold leading-snug tracking-tight text-slate-900 sm:mb-1.5 sm:text-lg">
          {post.title}
        </h3>
        <p className="mb-2.5 line-clamp-2 text-sm leading-relaxed text-slate-500 sm:mb-3">
          {post.description}
        </p>

        <div className="mt-auto flex items-end justify-between gap-2 sm:gap-3">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1 text-[11px] text-slate-400 sm:gap-x-3 sm:text-xs">
            <span className="inline-flex items-center gap-1 sm:gap-1.5">
              <Calendar className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
              <span className="truncate">{formatDate(post.date, lang)}</span>
            </span>
            <span className="inline-flex items-center gap-1 sm:gap-1.5">
              <Clock className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
              {t('blog.readingTime', { minutes: post.readingTime })}
            </span>
          </div>
          <span
            aria-hidden
            className="inline-block shrink-0 text-base font-semibold text-blue-600 transition-[color,transform] duration-200 translate-x-0 group-hover:translate-x-1 group-hover:text-blue-700"
          >
            →
          </span>
        </div>
      </div>
    </Link>
  );
};

export default BlogGuideCard;
